import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Plus, Download, Pencil, Trash2, FileText, FileSpreadsheet, AlertTriangle, ChevronDown, ChevronRight, Container } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, Btn, Field, Input, Select, Modal, ConfirmDialog } from "@/components/Primitives";
import { api, fmtNumber, formatApiErrorDetail, API, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const empty = {
  dispatch_date: new Date().toISOString().slice(0, 10),
  order_id: "", container_number: "", seal_number: "",
  shipment_id: "", shipment_number: "",
  vessel: "", voyage: "", booking_reference: "", export_destination: "",
  transporter: "", truck_registration: "",
  vgm_weight: "", container_size: "", tare_weight: "", weighbridge_number: "", stock_item_ids: [],
};

// Debounced duplicate-weighbridge warning for dispatch capture.
function WbDupWarning({ value, excludeId }) {
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    const wb = (value || "").trim();
    if (!wb) { setMsg(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/dispatch/check-duplicate", { params: { weighbridge_number: wb, exclude_id: excludeId || undefined } });
        if (r.data.duplicate) {
          const m = r.data.matches?.[0];
          const text = `Duplicate! Weighbridge # "${wb}" already captured${m?.order_number ? ` on dispatch for order ${m.order_number}` : ""}${m?.dispatch_date ? ` (${m.dispatch_date})` : ""}`;
          setMsg(text);
          toast.warning(text);
        } else setMsg(null);
      } catch { setMsg(null); }
    }, 600);
    return () => clearTimeout(t);
  }, [value, excludeId]);
  if (!msg) return null;
  return (
    <div className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1" data-testid="dispatch-wb-dup-warning">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {msg}
    </div>
  );
}

const CONTAINER_SIZES = ["20' GP", "40' GP", "40' HC", "45' HC"];

const MAX_ROWS = 400;

const StockRow = React.memo(function StockRow({ s, sel, onToggle }) {
  return (
    <tr className={`border-b ${sel ? "bg-emerald-50" : ""}`}>
      <td className="px-3 py-1.5">
        <input type="checkbox" checked={sel} onChange={(e) => onToggle(s.id, e.target.checked)}
               data-testid={`dispatch-stock-${s.id}`} />
      </td>
      <td className="px-3 py-1.5 font-mono text-xs">{s.order_number || "—"}</td>
      <td className="px-3 py-1.5 font-mono text-xs">{s.bundle_number || s.id.slice(0, 8)}</td>
      <td className="px-3 py-1.5 font-mono text-xs">{s.butt_size != null ? `${s.butt_size}mm` : "—"} · {s.length ? `${s.length}m` : "—"} · {s.colour || "—"}</td>
      <td className="px-3 py-1.5 text-xs">
        {s.pointed
          ? <span className="text-emerald-700 font-medium">Pointed ✓</span>
          : s.requires_pointing
            ? <span className="text-amber-700 font-medium" data-testid={`dispatch-needs-pointing-${s.id}`}>Needs pointing</span>
            : <span className="text-stone-400">—</span>}
      </td>
      <td className="px-3 py-1.5 text-xs">
        {s.bundled
          ? <span className="text-sky-700 font-medium">Bundled ✓</span>
          : s.requires_bundling
            ? <span className="text-amber-700 font-medium" data-testid={`dispatch-needs-bundling-${s.id}`}>Needs bundling</span>
            : <span className="text-stone-400">—</span>}
      </td>
      <td className="px-3 py-1.5 text-right font-mono">{fmtNumber(s.quantity)}</td>
      <td className="px-3 py-1.5 text-right font-mono">{fmtNumber(s.cbm, 3)}</td>
      <td className="px-3 py-1.5 text-right font-mono">{fmtNumber(s.weight)}</td>
    </tr>
  );
});

export default function Dispatch() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [allocatedStock, setAllocatedStock] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const [manifestSel, setManifestSel] = useState(() => new Set());
  const [stockFilter, setStockFilter] = useState({ q: "", order: "", colour: "", length: "", butt: "", pointed: "", bundled: "" });
  const [selectQty, setSelectQty] = useState("");
  const [shipments, setShipments] = useState([]);
  const [readyPlan, setReadyPlan] = useState(null); // locked container plan of the selected shipment
  const [collapsedCont, setCollapsedCont] = useState(() => new Set());
  const isAdmin = user?.role === "admin";

  const orderOptions = useMemo(() => [...new Set(allocatedStock.map((s) => s.order_number).filter(Boolean))].sort(), [allocatedStock]);
  const colourOptions = useMemo(() => [...new Set(allocatedStock.map((s) => s.colour).filter(Boolean))].sort(), [allocatedStock]);
  const lengthOptions = useMemo(() => [...new Set(allocatedStock.map((s) => Number(s.length)).filter(Boolean))].sort((a, b) => a - b), [allocatedStock]);
  const buttOptions = useMemo(() => [...new Set(allocatedStock.map((s) => Number(s.butt_size)).filter(Boolean))].sort((a, b) => a - b), [allocatedStock]);
  const filteredStock = useMemo(() => allocatedStock.filter((s) => {
    if (stockFilter.order && s.order_number !== stockFilter.order) return false;
    if (stockFilter.colour && s.colour !== stockFilter.colour) return false;
    if (stockFilter.length && Number(s.length) !== Number(stockFilter.length)) return false;
    if (stockFilter.butt && Number(s.butt_size) !== Number(stockFilter.butt)) return false;
    if (stockFilter.pointed === "yes" && !s.pointed) return false;
    if (stockFilter.pointed === "no" && s.pointed) return false;
    const isBundled = !!(s.bundle_number || "").toString().trim();
    if (stockFilter.bundled === "yes" && !isBundled) return false;
    if (stockFilter.bundled === "no" && isBundled) return false;
    const q = stockFilter.q.trim().toLowerCase();
    if (q) {
      const hay = [s.order_number, s.bundle_number, s.id, s.butt_size, s.length, s.colour, s.article_number]
        .filter((v) => v != null).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [allocatedStock, stockFilter]);

  const selSet = useMemo(() => new Set(form.stock_item_ids), [form.stock_item_ids]);
  const selTotals = useMemo(() => {
    let rows = 0, qty = 0, cbm = 0, wt = 0;
    for (const s of allocatedStock) {
      if (!selSet.has(s.id)) continue;
      rows += 1;
      qty += Number(s.quantity) || 0;
      cbm += Number(s.cbm) || 0;
      wt += Number(s.weight) || 0;
    }
    return { rows, qty, cbm, wt };
  }, [allocatedStock, selSet]);
  const toggleStock = useCallback((id, checked) => setForm((f) => ({
    ...f,
    stock_item_ids: checked ? [...f.stock_item_ids, id] : f.stock_item_ids.filter((x) => x !== id),
  })), []);

  // Locked shipment plan → group the (filtered) stock per container.
  const containerGroups = useMemo(() => {
    if (!readyPlan?.assignments?.length) return null;
    const cmap = {};
    readyPlan.assignments.forEach((a) => { cmap[a.stock_id] = a.container; });
    const n = readyPlan.containers?.length || 0;
    const groups = Array.from({ length: n }, (_, i) => ({ idx: i, label: `Container #${i + 1}`, items: [] }));
    const other = { idx: -1, label: "Not in the locked container plan", items: [] };
    for (const s of filteredStock) {
      const ci = cmap[s.id];
      if (ci == null || ci < 0 || ci >= n) other.items.push(s);
      else groups[ci].items.push(s);
    }
    if (other.items.length) groups.push(other);
    return groups.filter((g) => g.items.length > 0);
  }, [readyPlan, filteredStock]);

  const toggleContainerGroup = (g, checked) => setForm((f) => {
    const ids = g.items.map((s) => s.id);
    return {
      ...f,
      stock_item_ids: checked
        ? [...new Set([...f.stock_item_ids, ...ids])]
        : f.stock_item_ids.filter((x) => !ids.includes(x)),
    };
  });
  const toggleCollapse = (idx) => setCollapsedCont((p) => {
    const nxt = new Set(p);
    nxt.has(idx) ? nxt.delete(idx) : nxt.add(idx);
    return nxt;
  });

  const selectByQty = () => {
    const target = Math.floor(Number(selectQty));
    if (!target || target <= 0) {
      toast.error("Enter the number of poles to select");
      return;
    }
    const ids = [];
    let acc = 0;
    for (const s of filteredStock) {
      const q = Number(s.quantity) || 0;
      if (q <= 0 || acc + q > target) continue; // keep bundles whole — try smaller rows
      ids.push(s.id);
      acc += q;
      if (acc === target) break;
    }
    if (!ids.length) {
      toast.error("No rows fit within that quantity — the smallest bundle shown is bigger");
      return;
    }
    setForm((f) => ({ ...f, stock_item_ids: ids }));
    toast.success(acc === target
      ? `Selected exactly ${acc} poles (${ids.length} row(s))`
      : `Selected ${acc} of ${target} poles (${ids.length} row(s)) — bundles kept whole`);
  };

  const load = async () => {
    const r = await api.get("/dispatch");
    setItems(r.data);
    setManifestSel(new Set());
  };

  useEffect(() => {
    load();
    api.get("/shipments").then((r) => setShipments(r.data)).catch(() => {});
  }, []);

  const openModal = async () => {
    const r = await api.get("/stock", { params: { status: "allocated" } });
    // Show allocated bundles across all orders (containers can mix orders).
    setAllocatedStock(r.data.filter((s) => s.order_id));
    setStockFilter({ q: "", order: "", colour: "", length: "", butt: "", pointed: "", bundled: "" });
    setSelectQty("");
    setForm(empty);
    setOpen(true);
  };

  const toggleManifest = (id) => setManifestSel((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const save = async () => {
    try {
      if (form.stock_item_ids.length === 0) {
        toast.error("Select at least one bundle to dispatch");
        return;
      }
      const payload = {
        ...form,
        shipment_id: form.shipment_id || null,
        shipment_number: form.shipment_number || null,
        vgm_weight: form.vgm_weight === "" ? null : Number(form.vgm_weight),
        tare_weight: form.tare_weight === "" ? null : Number(form.tare_weight),
      };
      await api.post("/dispatch", payload);
      toast.success("Dispatch recorded");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveEdit = async () => {
    try {
      const { id, ...rest } = editItem;
      await api.patch(`/dispatch/${id}`, {
        dispatch_date: rest.dispatch_date,
        container_number: rest.container_number,
        seal_number: rest.seal_number,
        vessel: rest.vessel,
        voyage: rest.voyage,
        booking_reference: rest.booking_reference,
        export_destination: rest.export_destination,
        transporter: rest.transporter,
        truck_registration: rest.truck_registration,
        vgm_weight: rest.vgm_weight === "" ? null : rest.vgm_weight,
        container_size: rest.container_size || null,
        tare_weight: rest.tare_weight === "" ? null : rest.tare_weight,
        weighbridge_number: rest.weighbridge_number,
      });
      toast.success("Dispatch updated");
      setEditItem(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/dispatch/${delItem.id}`);
      toast.success("Dispatch reversed — stock returned to allocated");
      setDelItem(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <AppLayout>
      <PageHeader
        title="Dispatch"
        subtitle="Ship allocated bundles, capture container, seal, vessel and invoice references."
        actions={
          <>
            <Btn variant="secondary" as="a" href={`${API}/exports/dispatches.xlsx`} target="_blank" testid="export-dispatch">
              <Download className="h-4 w-4" /> Export
            </Btn>
            {can(user?.role, "dispatch:create") && (
              <Btn onClick={openModal} testid="new-dispatch-btn">
                <Plus className="h-4 w-4" /> New Dispatch
              </Btn>
            )}
          </>
        }
      />

      <Toolbar>
        {manifestSel.size > 0 ? (
          <div className="flex items-center gap-2">
            <a href={`${API}/dispatch/manifest.xlsx?ids=${Array.from(manifestSel).join(",")}`} target="_blank" rel="noreferrer" data-testid="generate-manifest-btn">
              <Btn><FileSpreadsheet className="h-4 w-4" /> Generate Manifest ({manifestSel.size})</Btn>
            </a>
            <a href={`${API}/dispatch/packing-lists.xlsx?ids=${Array.from(manifestSel).join(",")}`} target="_blank" rel="noreferrer" data-testid="export-packing-lists-btn">
              <Btn variant="secondary"><FileSpreadsheet className="h-4 w-4" /> Packing Lists — Excel ({manifestSel.size})</Btn>
            </a>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-stone-500" data-testid="manifest-hint">
            <FileSpreadsheet className="h-3.5 w-3.5" />
            Tick container(s) in the first column below to generate an SI Manifest or a combined Packing List workbook
          </div>
        )}
        <div className="text-xs text-stone-500 ml-auto">{items.length} dispatches</div>
      </Toolbar>

      <DataTable
        testid="dispatch-table"
        columns={[
          { header: "", render: (r) => (
            <input type="checkbox" checked={manifestSel.has(r.id)} onChange={() => toggleManifest(r.id)}
                   onClick={(e) => e.stopPropagation()} data-testid={`manifest-select-${r.id}`} />
          )},
          { header: "Date", key: "dispatch_date", mono: true },
          { header: "Order #", key: "order_number", mono: true },
          { header: "Container", mono: true, render: (r) => (
            <div className="text-xs">
              <div className="font-mono">{r.container_number || "—"}</div>
              <div className="text-stone-500">Seal: {r.seal_number || "—"}</div>
            </div>
          )},
          { header: "Vessel / Destination", render: (r) => (
            <div className="text-xs">
              <div>{r.vessel || "—"}</div>
              <div className="text-stone-500">{r.export_destination}</div>
            </div>
          )},
          { header: "Qty", mono: true, align: "right", render: (r) => fmtNumber(r.quantity_dispatched) },
          { header: "CBM", mono: true, align: "right", render: (r) => fmtNumber(r.cbm_dispatched, 3) },
          { header: "Weight", mono: true, align: "right", render: (r) => fmtNumber(r.weight_dispatched) + " kg" },
          { header: "VGM / Weighbridge", render: (r) => (
            <div className="text-xs">
              <div className="font-mono">{r.vgm_weight != null ? fmtNumber(r.vgm_weight) + " kg" : "—"}</div>
              <div className="text-stone-500">WB: {r.weighbridge_number || "—"}</div>
            </div>
          )},
          { header: "Actions", align: "right", render: (r) => (
            <div className="flex items-center gap-2 justify-end">
              <a href={`${API}/dispatch/${r.id}/packing-list.pdf`} target="_blank" rel="noreferrer"
                 onClick={(e) => e.stopPropagation()} className="text-emerald-800 hover:text-emerald-900"
                 title="Download Packing List" data-testid={`packing-list-${r.id}`}>
                <FileText className="h-4 w-4" />
              </a>
              <a href={`${API}/dispatch/${r.id}/packing-list.xlsx`} target="_blank" rel="noreferrer"
                 onClick={(e) => e.stopPropagation()} className="text-emerald-800 hover:text-emerald-900"
                 title="Download Packing List (Excel)" data-testid={`packing-list-xlsx-${r.id}`}>
                <FileSpreadsheet className="h-4 w-4" />
              </a>
              {can(user?.role, "dispatch:update") && (
                <button onClick={() => setEditItem({ ...r })} className="text-stone-500 hover:text-emerald-800" title="Edit" data-testid={`dispatch-edit-${r.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
              )}
              {isAdmin && (
                <button onClick={() => setDelItem(r)} className="text-stone-500 hover:text-red-700" title="Reverse / Delete" data-testid={`dispatch-delete-${r.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          )},
        ]}
        rows={items}
      />

      <Modal
        open={open} onClose={() => setOpen(false)} title="New Dispatch" size="lg" testid="dispatch-modal"
        footer={<><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn onClick={save} testid="dispatch-save">Dispatch</Btn></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Dispatch Date" required>
            <Input type="date" value={form.dispatch_date} onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })} />
          </Field>
          <Field label="VGM Weight (kg)">
            <Input type="number" step="0.01" value={form.vgm_weight} onChange={(e) => setForm({ ...form, vgm_weight: e.target.value })} data-testid="dispatch-vgm-weight" />
          </Field>
          <Field label="Container Size">
            <Select value={form.container_size} onChange={(e) => setForm({ ...form, container_size: e.target.value })} data-testid="dispatch-container-size">
              <option value="">—</option>
              {CONTAINER_SIZES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>

          <Field label="Container #">
            <Input value={form.container_number} onChange={(e) => setForm({ ...form, container_number: e.target.value })} />
          </Field>
          <Field label="Seal #">
            <Input value={form.seal_number} onChange={(e) => setForm({ ...form, seal_number: e.target.value })} />
          </Field>
          <Field label="Shipment (pre-fills vessel / booking / destination)">
            <Select value={form.shipment_id} data-testid="dispatch-shipment-select"
                    onChange={(e) => {
                      const s = shipments.find((x) => x.id === e.target.value);
                      setReadyPlan(s?.dispatch_ready && s?.dispatch_plan ? s.dispatch_plan : null);
                      setCollapsedCont(new Set());
                      setForm((f) => ({
                        ...f,
                        shipment_id: s?.id || "",
                        shipment_number: s?.shipment_number || "",
                        vessel: s ? (s.vessel || "") : f.vessel,
                        voyage: s ? (s.voyage || "") : f.voyage,
                        booking_reference: s ? (s.booking_reference || "") : f.booking_reference,
                        export_destination: s ? (s.export_destination || "") : f.export_destination,
                      }));
                    }}>
              <option value="">— none —</option>
              {shipments.filter((s) => s.status !== "closed" || s.id === form.shipment_id)
                .map((s) => <option key={s.id} value={s.id}>{s.shipment_number}{s.vessel ? ` — ${s.vessel}` : ""}{s.dispatch_ready ? " — DISPATCH READY" : ""}</option>)}
            </Select>
          </Field>
          <Field label="Vessel">
            <Input value={form.vessel} onChange={(e) => setForm({ ...form, vessel: e.target.value })} />
          </Field>
          <Field label="Voyage">
            <Input value={form.voyage} onChange={(e) => setForm({ ...form, voyage: e.target.value })} data-testid="dispatch-voyage" />
          </Field>

          <Field label="Booking Reference">
            <Input value={form.booking_reference} onChange={(e) => setForm({ ...form, booking_reference: e.target.value })} />
          </Field>
          <Field label="Export Destination">
            <Input value={form.export_destination} onChange={(e) => setForm({ ...form, export_destination: e.target.value })} />
          </Field>
          <Field label="Transporter">
            <Input value={form.transporter} onChange={(e) => setForm({ ...form, transporter: e.target.value })} />
          </Field>

          <Field label="Truck Registration">
            <Input value={form.truck_registration} onChange={(e) => setForm({ ...form, truck_registration: e.target.value })} />
          </Field>

          <Field label="Weighbridge Ticket #">
            <Input value={form.weighbridge_number} onChange={(e) => setForm({ ...form, weighbridge_number: e.target.value })} data-testid="dispatch-weighbridge-number" />
            <WbDupWarning value={form.weighbridge_number} />
          </Field>
          <Field label="Tare Weight (kg)">
            <Input type="number" step="0.01" value={form.tare_weight} onChange={(e) => setForm({ ...form, tare_weight: e.target.value })} data-testid="dispatch-tare-weight" />
          </Field>

          <div className="md:col-span-3">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="data-label">Stock to Dispatch — select bundles from any order (showing {filteredStock.length} of {allocatedStock.length})</div>
              <div className="flex items-center gap-2">
                <button type="button" className="text-xs text-emerald-700 hover:underline disabled:text-stone-300"
                  disabled={filteredStock.length === 0}
                  onClick={() => setForm((f) => ({ ...f, stock_item_ids: [...new Set([...f.stock_item_ids, ...filteredStock.map((s) => s.id)])] }))}
                  data-testid="dispatch-select-all-filtered">Select all shown</button>
                <span className="text-stone-300">·</span>
                <button type="button" className="text-xs text-stone-600 hover:underline disabled:text-stone-300"
                  disabled={form.stock_item_ids.length === 0}
                  onClick={() => setForm((f) => ({ ...f, stock_item_ids: [] }))}
                  data-testid="dispatch-clear-selection">Clear selection</button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
              <Input className="col-span-2" placeholder="Search order #, bundle, spec…" value={stockFilter.q}
                onChange={(e) => setStockFilter({ ...stockFilter, q: e.target.value })} data-testid="dispatch-stock-search" />
              <Select value={stockFilter.order} onChange={(e) => setStockFilter({ ...stockFilter, order: e.target.value })} data-testid="dispatch-stock-filter-order">
                <option value="">All orders</option>
                {orderOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </Select>
              <Select value={stockFilter.colour} onChange={(e) => setStockFilter({ ...stockFilter, colour: e.target.value })} data-testid="dispatch-stock-filter-colour">
                <option value="">All colours</option>
                {colourOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
              <Select value={stockFilter.length} onChange={(e) => setStockFilter({ ...stockFilter, length: e.target.value })} data-testid="dispatch-stock-filter-length">
                <option value="">All lengths</option>
                {lengthOptions.map((l) => <option key={l} value={l}>{l} m</option>)}
              </Select>
              <Select value={stockFilter.butt} onChange={(e) => setStockFilter({ ...stockFilter, butt: e.target.value })} data-testid="dispatch-stock-filter-butt">
                <option value="">All butt Ø</option>
                {buttOptions.map((b) => <option key={b} value={b}>{b} mm</option>)}
              </Select>
              <Select value={stockFilter.pointed} onChange={(e) => setStockFilter({ ...stockFilter, pointed: e.target.value })} data-testid="dispatch-stock-filter-pointed">
                <option value="">Pointed: all</option>
                <option value="yes">Pointed</option>
                <option value="no">Not pointed</option>
              </Select>
              <Select value={stockFilter.bundled} onChange={(e) => setStockFilter({ ...stockFilter, bundled: e.target.value })} data-testid="dispatch-stock-filter-bundled">
                <option value="">Bundled: all</option>
                <option value="yes">Bundled</option>
                <option value="no">Loose</option>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="dispatch-select-qty-bar">
              <span className="text-xs text-stone-600">Select a quantity from the shown stock:</span>
              <Input type="number" min="1" className="max-w-[110px]" placeholder="e.g. 100" value={selectQty}
                onChange={(e) => setSelectQty(e.target.value)} data-testid="dispatch-select-qty-input" />
              <Btn variant="secondary" onClick={selectByQty} disabled={filteredStock.length === 0}
                   testid="dispatch-select-qty-btn">Select qty</Btn>
              <span className="text-xs text-stone-400">Picks rows top-down, keeping bundles whole (replaces the current selection).</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 px-3 py-2 rounded border border-emerald-200 bg-emerald-50/60 text-sm" data-testid="dispatch-selected-totals">
              <span className="font-semibold text-emerald-900">Selected:</span>
              <span className="font-mono font-bold text-emerald-900" data-testid="dispatch-selected-qty">{fmtNumber(selTotals.qty)} poles</span>
              <span className="font-mono text-emerald-800">{fmtNumber(selTotals.rows)} row(s)</span>
              <span className="font-mono text-emerald-800">{fmtNumber(selTotals.cbm, 3)} m³</span>
              <span className="font-mono text-emerald-800">{fmtNumber(selTotals.wt)} kg</span>
            </div>
            {allocatedStock.length === 0 ? (
              <div className="text-stone-400 text-sm py-6 panel text-center">No allocated bundles available to dispatch.</div>
            ) : filteredStock.length === 0 ? (
              <div className="text-stone-400 text-sm py-6 panel text-center" data-testid="dispatch-no-match">No bundles match the current filters.</div>
            ) : containerGroups ? (
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="dispatch-container-groups">
                {containerGroups.map((g) => {
                  const ids = g.items.map((s) => s.id);
                  const selCount = ids.filter((id) => selSet.has(id)).length;
                  const allSel = selCount === ids.length && ids.length > 0;
                  const qty = g.items.reduce((a, s) => a + (Number(s.quantity) || 0), 0);
                  const open = !collapsedCont.has(g.idx);
                  return (
                    <div key={g.idx} className={`panel overflow-hidden ${g.idx === -1 ? "border-amber-300" : "border-cyan-200"}`} data-testid={`dispatch-container-group-${g.idx}`}>
                      <div className={`flex items-center gap-2 px-3 py-2 ${g.idx === -1 ? "bg-amber-50" : "bg-cyan-50/70"}`}>
                        <input type="checkbox" checked={allSel}
                               ref={(el) => { if (el) el.indeterminate = !allSel && selCount > 0; }}
                               onChange={(e) => toggleContainerGroup(g, e.target.checked)}
                               data-testid={`dispatch-container-select-${g.idx}`} />
                        <button type="button" className="flex items-center gap-1.5 flex-1 text-left"
                                onClick={() => toggleCollapse(g.idx)} data-testid={`dispatch-container-toggle-${g.idx}`}>
                          {open ? <ChevronDown className="h-3.5 w-3.5 text-stone-500" /> : <ChevronRight className="h-3.5 w-3.5 text-stone-500" />}
                          <Container className={`h-3.5 w-3.5 ${g.idx === -1 ? "text-amber-700" : "text-cyan-800"}`} />
                          <span className={`font-semibold text-sm ${g.idx === -1 ? "text-amber-900" : "text-cyan-900"}`}>{g.label}</span>
                          <span className="text-xs text-stone-500 font-mono">{qty} poles · {g.items.length} row(s)</span>
                          {selCount > 0 && <span className="text-xs font-semibold text-emerald-700 font-mono">{selCount}/{ids.length} selected</span>}
                        </button>
                      </div>
                      {open && (
                        <table className="min-w-full text-sm">
                          <thead className="bg-stone-100 border-y">
                            <tr>
                              <th className="px-3 py-1.5 w-8"></th>
                              <th className="px-3 py-1.5 text-left text-xs uppercase">Order #</th>
                              <th className="px-3 py-1.5 text-left text-xs uppercase">Bundle</th>
                              <th className="px-3 py-1.5 text-left text-xs uppercase">Spec</th>
                              <th className="px-3 py-1.5 text-left text-xs uppercase">Pointed</th>
                              <th className="px-3 py-1.5 text-left text-xs uppercase">Bundled</th>
                              <th className="px-3 py-1.5 text-right text-xs uppercase">Qty</th>
                              <th className="px-3 py-1.5 text-right text-xs uppercase">CBM</th>
                              <th className="px-3 py-1.5 text-right text-xs uppercase">Weight</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.items.slice(0, MAX_ROWS).map((s) => (
                              <StockRow key={s.id} s={s} sel={selSet.has(s.id)} onToggle={toggleStock} />
                            ))}
                          </tbody>
                        </table>
                      )}
                      {open && g.items.length > MAX_ROWS && (
                        <div className="px-3 py-2 text-xs text-stone-500 bg-stone-50 border-t">
                          Showing first {MAX_ROWS} of {g.items.length} rows — refine the filters. The container checkbox still selects all {g.items.length} rows.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="panel max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-stone-100 border-b sticky top-0">
                    <tr>
                      <th className="px-3 py-2 w-8"></th>
                      <th className="px-3 py-2 text-left text-xs uppercase">Order #</th>
                      <th className="px-3 py-2 text-left text-xs uppercase">Bundle</th>
                      <th className="px-3 py-2 text-left text-xs uppercase">Spec</th>
                      <th className="px-3 py-2 text-left text-xs uppercase">Pointed</th>
                      <th className="px-3 py-2 text-left text-xs uppercase">Bundled</th>
                      <th className="px-3 py-2 text-right text-xs uppercase">Qty</th>
                      <th className="px-3 py-2 text-right text-xs uppercase">CBM</th>
                      <th className="px-3 py-2 text-right text-xs uppercase">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStock.slice(0, MAX_ROWS).map((s) => (
                      <StockRow key={s.id} s={s} sel={selSet.has(s.id)} onToggle={toggleStock} />
                    ))}
                  </tbody>
                </table>
                {filteredStock.length > MAX_ROWS && (
                  <div className="px-3 py-2 text-xs text-stone-500 bg-stone-50 border-t" data-testid="dispatch-rows-capped">
                    Showing first {MAX_ROWS} of {filteredStock.length} rows — refine the filters to narrow down.
                    “Select all shown” still selects all {filteredStock.length} filtered rows.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Edit Dispatch (header fields only) */}
      {editItem && (
        <Modal
          open={!!editItem} onClose={() => setEditItem(null)} title={`Edit Dispatch · ${editItem.order_number || ""}`} size="lg"
          testid="dispatch-edit-modal"
          footer={<><Btn variant="secondary" onClick={() => setEditItem(null)}>Cancel</Btn><Btn onClick={saveEdit} testid="dispatch-edit-save">Save Changes</Btn></>}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Dispatch Date">
              <Input type="date" value={editItem.dispatch_date || ""} onChange={(e) => setEditItem({ ...editItem, dispatch_date: e.target.value })} />
            </Field>
            <Field label="VGM Weight (kg)">
              <Input type="number" step="0.01" value={editItem.vgm_weight ?? ""} onChange={(e) => setEditItem({ ...editItem, vgm_weight: e.target.value })} data-testid="dispatch-edit-vgm-weight" />
            </Field>
            <Field label="Weighbridge Ticket #">
              <Input value={editItem.weighbridge_number || ""} onChange={(e) => setEditItem({ ...editItem, weighbridge_number: e.target.value })} data-testid="dispatch-edit-weighbridge" />
              <WbDupWarning value={editItem.weighbridge_number} excludeId={editItem.id} />
            </Field>
            <Field label="Container Size">
              <Select value={editItem.container_size || ""} onChange={(e) => setEditItem({ ...editItem, container_size: e.target.value })} data-testid="dispatch-edit-container-size">
                <option value="">—</option>
                {CONTAINER_SIZES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Tare Weight (kg)">
              <Input type="number" step="0.01" value={editItem.tare_weight ?? ""} onChange={(e) => setEditItem({ ...editItem, tare_weight: e.target.value })} data-testid="dispatch-edit-tare-weight" />
            </Field>
            <Field label="Container #">
              <Input value={editItem.container_number || ""} onChange={(e) => setEditItem({ ...editItem, container_number: e.target.value })} data-testid="dispatch-edit-container" />
            </Field>
            <Field label="Seal #">
              <Input value={editItem.seal_number || ""} onChange={(e) => setEditItem({ ...editItem, seal_number: e.target.value })} />
            </Field>
            <Field label="Vessel">
              <Input value={editItem.vessel || ""} onChange={(e) => setEditItem({ ...editItem, vessel: e.target.value })} />
            </Field>
            <Field label="Voyage">
              <Input value={editItem.voyage || ""} onChange={(e) => setEditItem({ ...editItem, voyage: e.target.value })} data-testid="dispatch-edit-voyage" />
            </Field>
            <Field label="Booking Reference">
              <Input value={editItem.booking_reference || ""} onChange={(e) => setEditItem({ ...editItem, booking_reference: e.target.value })} />
            </Field>
            <Field label="Export Destination">
              <Input value={editItem.export_destination || ""} onChange={(e) => setEditItem({ ...editItem, export_destination: e.target.value })} />
            </Field>
            <Field label="Transporter">
              <Input value={editItem.transporter || ""} onChange={(e) => setEditItem({ ...editItem, transporter: e.target.value })} />
            </Field>
            <Field label="Truck Registration">
              <Input value={editItem.truck_registration || ""} onChange={(e) => setEditItem({ ...editItem, truck_registration: e.target.value })} />
            </Field>
          </div>
          <div className="text-xs text-stone-500 mt-3">Dispatched bundles & quantities can't be edited here. To change them, reverse this dispatch and create a new one.</div>
        </Modal>
      )}

      <ConfirmDialog
        open={!!delItem}
        onClose={() => setDelItem(null)}
        onConfirm={doDelete}
        title="Reverse Dispatch"
        confirmLabel="Reverse Dispatch"
        testid="dispatch-delete-confirm"
        message={delItem ? `Reverse the dispatch for order ${delItem.order_number}? Its ${delItem.quantity_dispatched} dispatched bundle(s) will return to 'allocated' and the order status will be recalculated. This cannot be undone.` : ""}
      />
    </AppLayout>
  );
}
